import {
  Box,
  FormControl,
  FormHelperText,
  type FormHelperTextProps,
  FormLabel,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material";
import { ReactNode } from "react";

import { useCommonTranslation } from "@dfp/react-common-translation";
import { IconInfoCircle } from "@dfp/react-icons";

import { getHelperText } from "./helper";

export type FieldWrapperProps = {
  name: string;
  label: string | undefined;
  labelId: string;
  labelHelpText: string | undefined;
  helperText: string | ReactNode | undefined;
  errorMessage: string | undefined;
  children: ReactNode;
  readOnly: boolean | undefined;
  required: boolean | undefined;
  helperTextProps: FormHelperTextProps | undefined;
  enableDeprecatedBottomGutter: boolean;
  hideOptionalLabel?: boolean;
};
export function FieldWrapper({
  label,
  labelId,
  labelHelpText,
  helperText,
  readOnly,
  required,
  errorMessage,
  children,
  helperTextProps,
  enableDeprecatedBottomGutter,
  hideOptionalLabel,
}: FieldWrapperProps) {
  const { commonTT } = useCommonTranslation();

  const helper = getHelperText(helperText, errorMessage, readOnly);

  return (
    <FormControl
      sx={{
        flexDirection: "column",
        gap: 0.5,
        ":not(:last-child)": {
          mb: enableDeprecatedBottomGutter ? 2 : 0,
        },
      }}
      disabled={readOnly === true}
      error={typeof errorMessage === "string" && readOnly !== true}
      fullWidth
      required={required}
      variant="outlined"
      size="medium"
    >
      {label ? (
        <FormLabel
          required={false}
          aria-required={required}
          sx={{ display: "inline-flex", gap: 1 }}
        >
          <Typography id={labelId} component="span">
            {label}
          </Typography>
          {!required && !readOnly && !hideOptionalLabel && (
            <Typography component="span" color="main">
              {/* Keeping here translation backup mainly for Storybook */}
              {commonTT("Input.optional")}
            </Typography>
          )}
          {labelHelpText ? (
            <Tooltip title={labelHelpText} placement="top">
              <IconButton
                size="small"
                disableRipple
                focusRipple
                sx={(theme) => ({
                  px: 0,
                  py: 0,
                  bottom: "-1px",
                  color: theme.palette.text.secondary,
                  "&:hover": { bgcolor: "transparent" },
                })}
              >
                <IconInfoCircle width={16} height={16} />
              </IconButton>
            </Tooltip>
          ) : null}
        </FormLabel>
      ) : null}
      <Box flex={1}>
        {children}
        {helper && (
          <FormHelperText {...helperTextProps}>{helper}</FormHelperText>
        )}
      </Box>
    </FormControl>
  );
}
